import { LocalAudioTrack, LocalVideoTrack, Track } from 'livekit-client';

// Experimental pilot budgets, measured separately from browser/device support.
export const CAMERA_CONSTRAINTS = {
  width: { ideal: 1280, max: 1280 },
  height: { ideal: 720, max: 720 },
  frameRate: { ideal: 30, max: 30 },
};
export const SCREEN_CONSTRAINTS = {
  width: { ideal: 1920, max: 1920 },
  height: { ideal: 1080, max: 1080 },
  frameRate: { ideal: 15, max: 15 },
};

// Application-owned tracks prevent SDK republishAllTracks(restartTracks=true)
// from reacquiring devices before Reconnected (LocalParticipant.ts, v2.22.3).
export const captureAudio = async ({
  noiseSuppression = true,
  microphoneId,
} = {}) => {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      noiseSuppression,
      echoCancellation: true,
      ...(microphoneId ? { deviceId: { exact: microphoneId } } : {}),
    },
    video: false,
  });
  return new LocalAudioTrack(stream.getAudioTracks()[0], undefined, true);
};
export const captureVideo = async ({ cameraId } = {}) => {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      ...CAMERA_CONSTRAINTS,
      ...(cameraId ? { deviceId: { exact: cameraId } } : {}),
    },
  });
  return new LocalVideoTrack(stream.getVideoTracks()[0], undefined, true);
};
export const screenConstraints = (quality = 'auto') => ({
  ...SCREEN_CONSTRAINTS,
  frameRate: {
    ideal: quality === 'motion' ? 30 : 15,
    max: quality === 'motion' ? 30 : 15,
  },
});
export const captureScreen = async ({
  screenAudio = false,
  quality = 'auto',
} = {}) => {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    audio: screenAudio,
    video: screenConstraints(quality),
  });
  const rawVideo = stream.getVideoTracks()[0];
  rawVideo.contentHint = quality === 'motion' ? 'motion' : 'detail';
  return [
    new LocalVideoTrack(rawVideo, undefined, true),
    ...stream
      .getAudioTracks()
      .map(track => new LocalAudioTrack(track, undefined, true)),
  ];
};

// TrackPublishOptions v2.22.3: use a single encoding to keep the pilot budget
// explicit; additional simulcast layers would add sender bandwidth and CPU.
export const encodingForSource = (source, quality = 'auto') => {
  if (source === Track.Source.Camera)
    return {
      simulcast: false,
      videoEncoding: { maxBitrate: 1_200_000, maxFramerate: 30 },
    };
  if (source === Track.Source.ScreenShare)
    return {
      simulcast: false,
      degradationPreference: {
        auto: 'balanced',
        text: 'maintain-resolution',
        motion: 'maintain-framerate',
      }[quality],
      screenShareEncoding: {
        maxBitrate: 2_000_000,
        // Capture controls 15/30 fps so quality can change without republishing.
        maxFramerate: 30,
      },
    };
  return {};
};
